import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { officeLocations } from '@/mocks/contactData';
import { useSiteData } from '@/contexts/SiteDataContext';

export default function ContactPage() {
  const { data } = useSiteData();
  const contactInfo = data.contact;
  const contactFaqs = data.faqs;
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('company_alt') as HTMLInputElement)?.value;
    if (honeypot && honeypot.trim()) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9euovauvvvit7qefndg', {
        method: 'POST',
        body: new URLSearchParams(formData as never),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (!response.ok || parsed?.code !== 'OK' || serverMsg.toLowerCase().includes('spam')) {
        setFormError(serverMsg || 'Something went wrong. Please try again.');
        setFormStatus('error');
        return;
      }

      setFormStatus('success');
      form.reset();
    } catch (err) {
      setFormError('Network error. Please check your connection and try again.');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <section className="relative py-20 md:py-28 bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/40 via-foreground-900 to-accent-900/30"></div>
          <div className="absolute top-10 left-10 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-accent-500/10 rounded-full blur-3xl"></div>
          <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm text-accent-300 text-xs font-semibold rounded-full mb-4">
              Get in Touch
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              We Would Love to <span className="gradient-text">Hear From You</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto">
              Whether you want to donate, volunteer, partner, or simply learn more — our team is here to help.
            </p>
          </div>
        </section>

        <section className="py-12 md:py-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  Send Us a Message
                </h2>

                {formStatus === 'success' ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
                    <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-line text-emerald-600 text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-xl font-bold text-emerald-800 mb-2">Message Sent!</h3>
                    <p className="text-emerald-600 text-sm mb-6">Thank you for reaching out. Our team will get back to you within 24-48 hours.</p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="px-6 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-full hover:bg-emerald-700 transition-colors whitespace-nowrap"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-foreground-700 text-sm font-medium mb-2">Full Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          placeholder="Your full name"
                          className="w-full px-4 py-3 bg-background-100 border border-background-200/70 rounded-xl text-foreground-900 text-sm placeholder-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-foreground-700 text-sm font-medium mb-2">Email Address</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          placeholder="you@example.com"
                          className="w-full px-4 py-3 bg-background-100 border border-background-200/70 rounded-xl text-foreground-900 text-sm placeholder-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="phone" className="block text-foreground-700 text-sm font-medium mb-2">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          placeholder="+91 98765 43210"
                          className="w-full px-4 py-3 bg-background-100 border border-background-200/70 rounded-xl text-foreground-900 text-sm placeholder-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        />
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-foreground-700 text-sm font-medium mb-2">Subject</label>
                        <select
                          id="subject"
                          name="subject"
                          required
                          className="w-full px-4 py-3 bg-background-100 border border-background-200/70 rounded-xl text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all appearance-none cursor-pointer"
                        >
                          <option value="">Select a subject</option>
                          <option value="Donation Inquiry">Donation Inquiry</option>
                          <option value="Volunteer Registration">Volunteer Registration</option>
                          <option value="Partnership / CSR">Partnership / CSR</option>
                          <option value="Media Inquiry">Media Inquiry</option>
                          <option value="General Question">General Question</option>
                          <option value="Feedback">Feedback</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-foreground-700 text-sm font-medium mb-2">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="Tell us how we can help you..."
                        className="w-full px-4 py-3 bg-background-100 border border-background-200/70 rounded-xl text-foreground-900 text-sm placeholder-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      ></textarea>
                      <p className="text-foreground-400 text-[11px] mt-1 text-right">Max 500 characters</p>
                    </div>

                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="absolute opacity-0 w-0 h-0"
                    />

                    {formError && (
                      <div className="flex items-center gap-2 text-rose-600 text-sm">
                        <i className="ri-error-warning-line"></i>
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {formStatus === 'loading' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              <div className="space-y-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                    Contact Information
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                        <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                      </div>
                      <p className="text-foreground-700 text-sm font-medium mb-1">Address</p>
                      <p className="text-foreground-500 text-sm leading-relaxed">{contactInfo.address}</p>
                    </div>
                    <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                      <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                        <i className="ri-phone-line text-accent-500 text-lg"></i>
                      </div>
                      <p className="text-foreground-700 text-sm font-medium mb-1">Phone</p>
                      <a href={`tel:${contactInfo.phone}`} className="text-foreground-500 text-sm hover:text-primary-600 transition-colors block">{contactInfo.phone}</a>
                      <p className="text-foreground-400 text-xs mt-1">Helpline: {contactInfo.helpline}</p>
                    </div>
                    <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                      <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mb-3">
                        <i className="ri-mail-line text-secondary-500 text-lg"></i>
                      </div>
                      <p className="text-foreground-700 text-sm font-medium mb-1">Email</p>
                      <a href={`mailto:${contactInfo.email}`} className="text-foreground-500 text-sm hover:text-primary-600 transition-colors break-all">{contactInfo.email}</a>
                    </div>
                    <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3">
                        <i className="ri-time-line text-emerald-500 text-lg"></i>
                      </div>
                      <p className="text-foreground-700 text-sm font-medium mb-1">Working Hours</p>
                      <p className="text-foreground-500 text-sm">{contactInfo.hours}</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl overflow-hidden border border-background-200/60 h-[280px]">
                  <iframe
                    src={contactInfo.mapUrl || 'https://maps.google.com/maps?q=17.40445327758789,78.50751495361328&z=17&output=embed'}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Happy Faces Hyderabad Office Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              Our Offices Across India
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {officeLocations.map((office) => (
                <div
                  key={office.city}
                  className={`p-5 rounded-xl border transition-all duration-300 hover:-translate-y-1 ${
                    office.isHeadquarters
                      ? 'bg-primary-50 border-primary-200/60'
                      : 'bg-background-50 border-background-200/60 hover:border-primary-200/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${office.isHeadquarters ? 'bg-primary-100' : 'bg-background-200/70'}`}>
                      <i className={`ri-building-line ${office.isHeadquarters ? 'text-primary-500' : 'text-foreground-500'} text-sm`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-900">{office.city}</h3>
                      {office.isHeadquarters && (
                        <span className="text-primary-600 text-[10px] font-semibold">Headquarters</span>
                      )}
                    </div>
                  </div>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-3">{office.address}</p>
                  <a href={`tel:${office.phone}`} className="flex items-center gap-1.5 text-foreground-600 hover:text-primary-600 text-xs mb-1.5 transition-colors">
                    <i className="ri-phone-line text-foreground-400"></i>{office.phone}
                  </a>
                  <a href={`mailto:${office.email}`} className="flex items-center gap-1.5 text-foreground-600 hover:text-primary-600 text-xs transition-colors break-all">
                    <i className="ri-mail-line text-foreground-400"></i>{office.email}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {contactFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-xl border border-background-200/60 overflow-hidden transition-all duration-300"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left"
                  >
                    <span className="text-foreground-900 text-sm md:text-base font-semibold pr-4">{faq.question}</span>
                    <div className={`w-7 h-7 rounded-full bg-background-200/70 flex items-center justify-center shrink-0 transition-all duration-300 ${activeFaq === index ? 'bg-primary-100 rotate-180' : ''}`}>
                      <i className={`ri-arrow-down-s-line ${activeFaq === index ? 'text-primary-500' : 'text-foreground-400'} text-sm transition-colors`}></i>
                    </div>
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-300"
                    style={{ maxHeight: activeFaq === index ? '500px' : '0px' }}
                  >
                    <p className="px-4 md:px-5 pb-4 md:pb-5 text-foreground-500 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-primary-500">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">
              Ready to Make a Difference?
            </h2>
            <p className="text-white/70 text-sm md:text-base max-w-lg mx-auto mb-6">
              Whether you want to donate, volunteer, or start a fundraiser — we are here to help you create impact.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/donate"
                className="inline-flex items-center gap-2 px-7 py-3 bg-white text-primary-600 font-semibold rounded-full hover:bg-white/90 transition-all duration-300 whitespace-nowrap shadow-md"
              >
                <i className="ri-heart-line text-base"></i>
                Donate Now
              </Link>
              <Link
                to="/volunteer"
                className="inline-flex items-center gap-2 px-7 py-3 bg-white/15 backdrop-blur-sm text-white font-semibold rounded-full hover:bg-white/25 transition-all duration-300 whitespace-nowrap border border-white/20"
              >
                <i className="ri-user-add-line text-base"></i>
                Become a Volunteer
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}